import { type CoachConfig, DEFAULT_COACH_CONFIG } from "../engine/coach-params";

// Coach parameters, persisted to localStorage as overrides on the engine
// defaults. Separate from the coach engine (which owns only the defaults) and
// from play prefs (store/prefs.ts). Restored on load so bench tuning survives a
// reload; `merge` drops stale/garbage keys (type-checked) so an old-schema save
// can't inject a value the current build wouldn't accept.

const KEY = "drop-notes.coach-config";

export function loadCoachConfig(): CoachConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return merge(JSON.parse(raw) as Partial<CoachConfig>);
  } catch {
    // ignore corrupt/blocked storage — fall through to defaults
  }
  return { ...DEFAULT_COACH_CONFIG };
}

export function saveCoachConfig(cfg: CoachConfig): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(cfg));
  } catch {
    // ignore — config simply won't persist
  }
}

/** Merge saved values over defaults, keeping only keys whose type still matches
 *  (every CoachConfig field is a number or boolean), so stale/garbage is dropped. */
function merge(saved: Partial<CoachConfig>): CoachConfig {
  const clean: Partial<Record<keyof CoachConfig, number | boolean>> = {};
  for (const key of Object.keys(DEFAULT_COACH_CONFIG) as (keyof CoachConfig)[]) {
    const v = saved[key];
    if (typeof v === typeof DEFAULT_COACH_CONFIG[key]) clean[key] = v as number | boolean;
  }
  return { ...DEFAULT_COACH_CONFIG, ...clean } as CoachConfig;
}
